import APIConstant from 'constants/ApiConstant';
import notify from 'constants/notificationConstants';
import { makeAutoObservable, runInAction } from 'mobx';
import { deleteRequest, getRequest, postRequest, putRequest } from 'utils/apiUtils';
import { getValidDataFromResponse, isValidArray, sendNotification } from 'utils/utilities';

/**
 * The backend may hand a template back either flat (`{ ...header, questions: [] }`)
 * or split (`{ template: {...}, questions: [] }`). Normalize both into the flat
 * shape `DynamicSurveyForm` consumes.
 */
const normalizeTemplateResult = result => {
	if (!result) return null;

	const payload = Array.isArray(result) ? result[0] : result;

	if (!payload) return null;

	const header = payload.template && typeof payload.template === 'object' ? payload.template : payload;
	const questions = payload.questions || payload.template_questions || header.questions || [];

	return { ...header, questions: isValidArray(questions) ? questions : [] };
};

class SurveyTemplateStore {
	surveyTemplates = [];
	isLoadingTemplates = false;

	templateDetails = null;
	isLoadingTemplateDetails = false;

	isSavingTemplate = false;
	deleteTemplateRowId = null;

	facilityAssignments = [];
	isLoadingFacilityAssignments = false;
	isAssigningTemplate = false;
	unassignRowId = null;

	facilitiesByTemplate = [];
	isLoadingFacilitiesByTemplate = false;

	constructor() {
		makeAutoObservable(this);
	}

	getSurveyTemplates = async (params = {}) => {
		try {
			this.setIsLoadingTemplates(true);

			const { response } = await getRequest({ url: APIConstant.GET_SURVEY_TEMPLATES, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			if (!error && isValidArray(result)) {
				this.setSurveyTemplates(result);
			} else {
				this.setSurveyTemplates();
			}

			this.setIsLoadingTemplates();
			return this.surveyTemplates;
		} catch (error) {
			console.log('error :>> ', error);
			this.setSurveyTemplates();
			this.setIsLoadingTemplates();
			return [];
		}
	};

	getSurveyTemplateById = async templateId => {
		try {
			this.setIsLoadingTemplateDetails(true);
			this.setTemplateDetails(null);

			const { response } = await getRequest({ url: `${APIConstant.SURVEY_TEMPLATE}/${templateId}` });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			const template = error ? null : normalizeTemplateResult(result);

			this.setTemplateDetails(template);
			this.setIsLoadingTemplateDetails();

			return template;
		} catch (error) {
			console.log('error :>> ', error);
			this.setTemplateDetails(null);
			this.setIsLoadingTemplateDetails();
			return null;
		}
	};

	createSurveyTemplate = async body => {
		try {
			this.setIsSavingTemplate(true);

			const { response } = await postRequest({ url: APIConstant.SURVEY_TEMPLATE, payload: body });

			const { result, error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setIsSavingTemplate();
			return result;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setIsSavingTemplate();
			throw error;
		}
	};

	updateSurveyTemplate = async (templateId, body) => {
		try {
			this.setIsSavingTemplate(true);

			const { response } = await putRequest({ url: `${APIConstant.SURVEY_TEMPLATE}/${templateId}`, payload: body });

			const { result, error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setIsSavingTemplate();
			return result;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setIsSavingTemplate();
			throw error;
		}
	};

	deleteSurveyTemplate = async (templateId, row) => {
		try {
			this.setDeleteTemplateRowId(row);

			const { response } = await deleteRequest({ url: `${APIConstant.SURVEY_TEMPLATE}/${templateId}` });

			const { error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setDeleteTemplateRowId(null);
			return true;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setDeleteTemplateRowId(null);
			throw error;
		}
	};

	/** Active templates assigned to a facility - drives the assignments table. */
	getFacilityAssignments = async (facilityId, params = {}) => {
		try {
			this.setIsLoadingFacilityAssignments(true);

			const { response } = await getRequest({ url: `${APIConstant.SURVEY_TEMPLATES_BY_FACILITY}/${facilityId}`, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			if (!error && isValidArray(result)) {
				this.setFacilityAssignments(result);
			} else {
				this.setFacilityAssignments();
			}

			this.setIsLoadingFacilityAssignments();
			return this.facilityAssignments;
		} catch (error) {
			console.log('error :>> ', error);
			this.setFacilityAssignments();
			this.setIsLoadingFacilityAssignments();
			return [];
		}
	};

	/** Facilities a template is currently assigned to. */
	getFacilitiesByTemplate = async templateId => {
		try {
			this.setIsLoadingFacilitiesByTemplate(true);

			const { response } = await getRequest({ url: `${APIConstant.SURVEY_FACILITIES_BY_TEMPLATE}/${templateId}` });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			if (!error && isValidArray(result)) {
				this.setFacilitiesByTemplate(result);
			} else {
				this.setFacilitiesByTemplate();
			}

			this.setIsLoadingFacilitiesByTemplate();
			return this.facilitiesByTemplate;
		} catch (error) {
			console.log('error :>> ', error);
			this.setFacilitiesByTemplate();
			this.setIsLoadingFacilitiesByTemplate();
			return [];
		}
	};

	assignTemplateToFacility = async body => {
		try {
			this.setIsAssigningTemplate(true);

			const { response } = await postRequest({ url: APIConstant.SURVEY_FACILITY_ASSIGNMENT, payload: body });

			const { result, error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setIsAssigningTemplate();
			return result;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setIsAssigningTemplate();
			throw error;
		}
	};

	unassignTemplateFromFacility = async (assignmentId, row) => {
		try {
			this.setUnassignRowId(row);

			const { response } = await deleteRequest({ url: `${APIConstant.SURVEY_FACILITY_ASSIGNMENT}/${assignmentId}` });

			const { error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setUnassignRowId(null);
			return true;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setUnassignRowId(null);
			throw error;
		}
	};

	setSurveyTemplates = (data = []) => {
		runInAction(() => {
			this.surveyTemplates = data;
		});
	};

	setIsLoadingTemplates = (value = false) => {
		runInAction(() => {
			this.isLoadingTemplates = value;
		});
	};

	setTemplateDetails = (data = null) => {
		runInAction(() => {
			this.templateDetails = data;
		});
	};

	setIsLoadingTemplateDetails = (value = false) => {
		runInAction(() => {
			this.isLoadingTemplateDetails = value;
		});
	};

	setIsSavingTemplate = (value = false) => {
		runInAction(() => {
			this.isSavingTemplate = value;
		});
	};

	setDeleteTemplateRowId = (value = null) => {
		runInAction(() => {
			this.deleteTemplateRowId = value;
		});
	};

	setFacilityAssignments = (data = []) => {
		runInAction(() => {
			this.facilityAssignments = data;
		});
	};

	setIsLoadingFacilityAssignments = (value = false) => {
		runInAction(() => {
			this.isLoadingFacilityAssignments = value;
		});
	};

	setIsAssigningTemplate = (value = false) => {
		runInAction(() => {
			this.isAssigningTemplate = value;
		});
	};

	setUnassignRowId = (value = null) => {
		runInAction(() => {
			this.unassignRowId = value;
		});
	};

	setFacilitiesByTemplate = (data = []) => {
		runInAction(() => {
			this.facilitiesByTemplate = data;
		});
	};

	setIsLoadingFacilitiesByTemplate = (value = false) => {
		runInAction(() => {
			this.isLoadingFacilitiesByTemplate = value;
		});
	};
}

export default SurveyTemplateStore;
